import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    PartyPopper, Clock, Users, Sparkles, Eye, X, ArrowRight, Loader2,
} from "lucide-react";
import api from "@/lib/api";

/**
 * FirstJobSpotlight — iter-160.
 *
 * Pins the employer's first-posted role at the top of the Employer
 * Dashboard for 7 days (`EMPLOYER_SPOTLIGHT_WINDOW_DAYS`) with a
 * celebratory "You're live" banner + live counters for AI matches,
 * applications, and passport requests. Auto-hides after the window
 * expires or the user explicitly dismisses it (owner-scoped stamp
 * on the user doc so a reload doesn't resurrect it).
 *
 * Rendering order on EmployerDashboardV2:
 *   1. WelcomeChecklist (3-step onboarding)
 *   2. FirstJobSpotlight (this component — only after a job exists)
 *   3. TrialBanner (trial countdown)
 *   4. Dashboard proper
 */
export const FirstJobSpotlight = () => {
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dismissBusy, setDismissBusy] = useState(false);

    const load = () => {
        api.get("/employer/first-job-spotlight")
            .then((r) => setState(r.data))
            .catch(() => { /* ignore — hide */ })
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const dismiss = async () => {
        setDismissBusy(true);
        try {
            await api.post("/employer/first-job-spotlight/dismiss", {});
        } catch { /* ignore */ }
        setState({ active: false, reason: "dismissed_by_user" });
    };

    if (loading || !state || !state.active) return null;

    const { job, metrics, window: win } = state;
    const totalSignal = (metrics.ai_matches || 0) + (metrics.applications || 0) + (metrics.passport_requests || 0);

    return (
        <div
            className="mb-4 rounded-xl border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-500/[0.08] via-background to-background p-5"
            data-testid="first-job-spotlight"
            data-job-id={job.id}
            data-days-remaining={win.days_remaining}
        >
            <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
                <div className="shrink-0 h-10 w-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center">
                    <PartyPopper size={18} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="rounded-full bg-emerald-500 text-white px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider" data-testid="spotlight-live-pill">
                            You&apos;re live
                        </span>
                        <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" data-testid="spotlight-window-countdown">
                            <Clock size={11} />
                            {win.days_remaining > 0
                                ? `${win.days_remaining} day${win.days_remaining === 1 ? "" : "s"} left`
                                : `${win.hours_remaining}h left`}
                        </span>
                    </div>
                    <h3 className="mt-1.5 font-heading font-semibold text-lg sm:text-xl text-foreground truncate" data-testid="spotlight-job-title">
                        {job.title}
                    </h3>
                    <p className="mt-0.5 text-xs sm:text-sm text-muted-foreground">
                        {[job.company, job.location].filter(Boolean).join(" · ")}
                    </p>
                </div>
                <button
                    onClick={dismiss}
                    disabled={dismissBusy}
                    aria-label="Dismiss spotlight"
                    className="shrink-0 text-muted-foreground hover:text-foreground transition-colors p-1 disabled:opacity-60"
                    data-testid="spotlight-dismiss"
                >
                    {dismissBusy ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
                </button>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-3" data-testid="spotlight-metrics">
                <Metric
                    icon={Sparkles}
                    value={metrics.ai_matches}
                    label="AI matches"
                    tone="emerald"
                    testId="spotlight-metric-matches"
                />
                <Metric
                    icon={Users}
                    value={metrics.applications}
                    label="Applications"
                    tone="violet"
                    testId="spotlight-metric-applications"
                />
                <Metric
                    icon={Eye}
                    value={metrics.passport_requests}
                    label="Passport requests"
                    tone="amber"
                    testId="spotlight-metric-passports"
                />
            </div>

            <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                <p className="text-xs text-muted-foreground max-w-xl leading-relaxed">
                    {totalSignal > 0
                        ? `${totalSignal} ${totalSignal === 1 ? "signal" : "signals"} on this role so far — tap in to review candidates.`
                        : "We're still matching candidates to your brief. New matches arrive within hours."}
                </p>
                <Link
                    to={`/employer/jobs/${job.id}`}
                    className="btn-green inline-flex items-center gap-1.5 text-xs sm:text-sm"
                    data-testid="spotlight-open-job-btn"
                >
                    Open role <ArrowRight size={13} />
                </Link>
            </div>
        </div>
    );
};

const TONE_CLASSES = {
    emerald: { icon: "text-emerald-600 bg-emerald-500/10", border: "border-emerald-500/20" },
    violet:  { icon: "text-violet-600 bg-violet-500/10", border: "border-violet-500/20" },
    amber:   { icon: "text-amber-600 bg-amber-500/10", border: "border-amber-500/20" },
};

const Metric = ({ icon: Icon, value, label, tone, testId }) => {
    const t = TONE_CLASSES[tone] || TONE_CLASSES.emerald;
    return (
        <div
            className={`rounded-lg border ${t.border} bg-background/70 p-3 flex items-center gap-3`}
            data-testid={testId}
        >
            <div className={`shrink-0 h-9 w-9 rounded-lg flex items-center justify-center ${t.icon}`}>
                <Icon size={16} />
            </div>
            <div className="min-w-0">
                <div className="font-heading font-bold text-xl text-foreground tabular-nums" data-testid={`${testId}-value`}>
                    {Number(value || 0)}
                </div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-wider leading-tight">
                    {label}
                </div>
            </div>
        </div>
    );
};

export default FirstJobSpotlight;
